import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AssuranceReportDraftingPage.module.scss';
import { Button } from '@zurich-primitives/button';
import { useAppSelector } from 'app';
import { useDispatch } from 'react-redux';
import { Link } from '@zurich-data-dsm-icons/action.link';
import {
  getBusinessArea,
  getService,
  setGeneratingExtraction,
  setRegenerateExtraction
} from 'app/redux';
import { getAssuranceReportDraftingDoc } from 'integration';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as FileUploadIcon } from 'app/assets/Icons/FileUpload.svg';
import { ReactComponent as DeleteIcon } from 'app/assets/Icons/Delete.svg';
import { ReactComponent as AssuranceDraft } from 'app/assets/Icons/AssuranceDraft.svg';
import { ReactComponent as GenerateReport } from 'app/assets/Icons/GenerateReport.svg';
import { ReactComponent as CheckIcon } from 'app/assets/Icons/Check.svg';
import { ReactComponent as DocFile } from 'app/assets/Icons/DOCFile.svg';
import {
  getAllowedMimeTypes,
  handleFileValidation,
  displayToastError
} from 'app/utils/functions';
import { SERVICE_NAMES } from 'app/utils/consts';
import config from 'config';

const LENGHT_TEXT_DISPLAY = 35;

export const AssuranceReportDraftingView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea) ?? 'compliance';

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [torDoc, setTorDoc] = useState<File | undefined>(undefined);
  const [progress, setProgress] = useState(0);
  const [testIds, setTestIds] = useState<string | undefined>(undefined);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        const rejectedFile = fileRejections[0];
        if (
          rejectedFile.errors.some(
            (error: any) => error.code === 'file-too-large'
          )
        ) {
          setErrorMessage('File size exceeds the maximum limit.');
        } else {
          setErrorMessage('File type not allowed.');
        }
        setUploadedFiles([]);
        displayToastError('File upload failed');
      } else if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        const validation = handleFileValidation(
          file,
          SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING
        );

        if (validation.valid) {
          setUploadedFiles([file]);
          setErrorMessage(null);
        } else {
          setErrorMessage(validation.error ?? 'Invalid file');
          setUploadedFiles([]);
          displayToastError('File validation failed', validation.error);
        }
      }
    },
    [errorMessage]
  );

  const allowedMimeTypes = getAllowedMimeTypes(
    SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: allowedMimeTypes,
    maxSize: 10 * 1024 * 1024,
    onDropRejected: () => setErrorMessage('File type not allowed.')
  });

  const handleDeleteFile = () => {
    setUploadedFiles([]);
    setErrorMessage(null);
  };

  const handleGenerateReport = async () => {
    if (uploadedFiles.length === 0) {
      setErrorMessage('Please upload a ToR document first.');
      return;
    }

    if (!testIds || testIds.trim() === '') {
      setErrorMessage('Please enter Test IDs.');
      return;
    }

    const uploadedFile = uploadedFiles[0];

    const validation = handleFileValidation(
      uploadedFile,
      SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING
    );
    if (validation.valid) {
      // continue
    } else {
      setErrorMessage(validation.error ?? 'Invalid file');
      displayToastError('File validation failed', validation.error);
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    dispatch(setGeneratingExtraction(true));
    dispatch(setRegenerateExtraction(true));

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + Math.random() * 15;
      });
    }, 500);

    const body = new FormData();
    body.append('file', uploadedFile, uploadedFile.name);
    body.append('testIDs', testIds.trim());

    try {
      const response = await getAssuranceReportDraftingDoc(
        String(currentBusinessArea),
        String(currentService),
        body
      );
      const uint8Array = new Uint8Array(response.file);
      const blob = new Blob([uint8Array], { type: response.fileType });
      const file = new File([blob], response.fileName, {
        type: response.fileType
      });
      setTorDoc(file);
      setProgress(100);
    } catch (error: unknown) {
      let detailedMessage: string | undefined;
      if (error && typeof error === 'object') {
        const anyErr = error as any;
        detailedMessage =
          anyErr?.response?.data?.message ?? anyErr?.message ?? undefined;
      }
      const fallbackMessage = 'Failed to generate draft report.';
      const userMessage = detailedMessage ?? fallbackMessage;
      setErrorMessage(userMessage);

      displayToastError('Generation failed', userMessage);
    } finally {
      clearInterval(progressInterval);
      setIsGenerating(false);
      dispatch(setGeneratingExtraction(false));
    }
  };

  const handleDownloadClick = () => {
    if (!torDoc) return;
    const url = URL.createObjectURL(torDoc);
    const link = document.createElement('a');
    link.href = url;
    link.download = torDoc.name;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleOpenAPI = (): void => {
    if (currentService) {
      navigate(config.APP.PATH.API.replace(':api', currentService));
    }
  };

  const renderReportStatusContent = (): React.ReactNode => {
    if (isGenerating) {
      return (
        <div className={styles['generating-report-container']}>
          <div className={styles['progress-circle']}>
            <span className={styles['progress-text']}>
              {Math.round(progress)}%
            </span>
          </div>
          <span className={styles['generating-title']}>Generating Report</span>
          <p className={styles['generating-text']}>...</p>
          <Button
            variant="secondary"
            disabled
            className={styles['download-btn']}
          >
            <Button.Label>Download Draft Report</Button.Label>
          </Button>
        </div>
      );
    }
    if (torDoc) {
      return (
        <div>
          <div className={styles['completed-badge']}>Report Completed</div>
          <div className={styles['completed-container']}>
            <div className={styles['file-info-display']}>
              <div className={styles['file-icon']}>
                <DocFile width={24} height={24} />
              </div>
              <span> {torDoc.name}</span>
            </div>
            <div className={styles['progress-bar-container']}>
              <div className={styles['progress-bar-filled']} />
            </div>
            <Button
              variant="primary"
              onClick={handleDownloadClick}
              className={styles['download-btn']}
            >
              <Button.Label>Download Draft Report</Button.Label>
            </Button>
          </div>
        </div>
      );
    }
    if (uploadedFiles.length > 0) {
      return (
        <div className={styles['empty-state']}>
          <div className={styles['empty-icon']}>
            <AssuranceDraft width={150} height={150} />
          </div>
          <h4 className={styles['empty-title']}>Your Generated Report Draft</h4>
          <p className={styles['empty-text']}>
            Once your report draft is ready, it will appear below. Click the
            download link to save it to your device. If you need to make any
            changes, feel free to upload a different document and generate a new
            report.
          </p>
          <Button
            variant="secondary"
            disabled
            className={styles['download-btn']}
          >
            <Button.Label>Download Draft Report</Button.Label>
          </Button>
        </div>
      );
    }
    return (
      <div className={styles['empty-state']}>
        <div className={styles['empty-icon']}>
          <AssuranceDraft width={150} height={150} />
        </div>
        <span className={styles['empty-title']}>
          No Report Draft Available yet.
        </span>
        <p className={styles['empty-text']}>
          It looks like you haven&apos;t generated a report draft yet. To get
          started, please upload your Terms of Reference (ToR) document
          following the first steps in this page.
        </p>
        <Button variant="secondary" disabled className={styles['download-btn']}>
          <Button.Label>Download Draft Report</Button.Label>
        </Button>
      </div>
    );
  };

  return (
    <div className={styles['ard-container']}>
      <div
        className={styles['header-row']}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24
        }}
      >
        <span className={styles['main-title']}>
          Welcome to Assurance Report Draft Generator
        </span>
        <Button onClick={handleOpenAPI} variant="tertiary">
          <Button.Icon>
            <Link />
          </Button.Icon>
          <Button.Label>Use our API version</Button.Label>
        </Button>
      </div>
      <div className={styles['main-content']}>
        <div className={styles['left-column']}>
          <p className={styles.description}>
            Artie is my name and I will support you in generating the first
            draft of your Assurance report. My purpose is to simplify the
            Compliance Assurance reporting process by allowing you to upload (A)
            your Terms of Reference (ToR) document and (B) to provide the Test
            Execution ID and receive a draft report in return. Just follow the
            steps below to get started.
          </p>
          <p className={styles.description}>
            <em>Artie stands for AI Report Task Intelligence Engine</em>
          </p>
          <ol className={styles['steps-list']}>
            <li className={styles['step-item']}>
              <p className={styles['step-title']}>Upload your ToR Document</p>
              <ul className={styles['requirements-list']}>
                <li className={styles['requirement-item']}>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>{' '}
                  Accepted file format: <strong>DOCX</strong>
                </li>
                <li className={styles['requirement-item']}>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>{' '}
                  File size limit: <strong>up to 10 MB per file</strong>
                </li>
                <li className={styles['requirement-item']}>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>{' '}
                  Mandatory documents:
                  <strong>.docx file containing the Terms of Reference</strong>
                </li>
              </ul>

              <div className={styles['upload-section-title']}>
                Upload your Document here
              </div>

              <div
                {...getRootProps()}
                className={`${styles['upload-box']} ${
                  isDragActive ? styles['drag-active'] : ''
                }`}
              >
                <input {...getInputProps()} />
                {uploadedFiles.length === 0 ? (
                  <>
                    <Button
                      variant="secondary"
                      className={styles['browse-btn']}
                    >
                      <Button.Icon>
                        <FileUploadIcon />
                      </Button.Icon>
                      Browse file
                    </Button>
                    <div className={styles['upload-desc']}>
                      Browse a file or drag it here to upload
                    </div>
                    {errorMessage && (
                      <div className={styles.error}>{errorMessage}</div>
                    )}
                  </>
                ) : (
                  <div className={styles['uploaded-file-card']}>
                    <div className={styles['file-details']}>
                      <CheckIcon className={styles['file-status-icon']} />
                      <p className={styles['file-name']}>
                        {uploadedFiles[0].name.length > LENGHT_TEXT_DISPLAY
                          ? uploadedFiles[0].name.slice(
                              0,
                              LENGHT_TEXT_DISPLAY
                            ) + '...'
                          : uploadedFiles[0].name}
                      </p>
                    </div>

                    <Button
                      variant="secondary"
                      onClick={handleDeleteFile}
                      className={styles['delete-file-btn']}
                      aria-label="Delete uploaded file"
                    >
                      <DeleteIcon />
                    </Button>
                  </div>
                )}
              </div>
            </li>
            <li className={styles['step-item']}>
              <p className={styles['step-title']}>Enter Cosmos Test Ids</p>
              <p className={styles['step-description']}>
                The test execution does not have to be completed. The Test-ID is
                being used to locate the issues in ISM. The issues can remain in
                draft (status New) for Artie to run.
              </p>
              <ul className={styles['requirements-list']}>
                <li className={styles['requirement-item']}>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>{' '}
                  Please use the following format of Test-[6 digits]: example{' '}
                  <strong>Test-123456</strong>
                </li>
                <li className={styles['requirement-item']}>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>{' '}
                  If you have multiple IDs, please separate them with commas:{' '}
                  <strong>Test-123456, Test-234567</strong>
                </li>
              </ul>

              <input
                type="text"
                placeholder="Comma separated Test IDs (e.g. Test-123456, Test-234567)"
                className={styles.input}
                spellCheck="false"
                value={testIds}
                onChange={(e) => setTestIds(e.target.value)}
              />
            </li>
            <li className={styles['step-item']}>
              <div className={styles['generate-section']}>
                <p className={styles['step-title']}>Generate Report Draft</p>
                <Button
                  className={styles['generate-btn']}
                  variant="secondary"
                  disabled={
                    isGenerating ||
                    uploadedFiles.length === 0 ||
                    !testIds ||
                    testIds.trim() === ''
                  }
                  onClick={handleGenerateReport}
                >
                  <Button.Icon>
                    <GenerateReport />
                  </Button.Icon>
                  <Button.Label>
                    {isGenerating ? 'Generating...' : 'Generate Report'}
                  </Button.Label>
                </Button>
              </div>
            </li>
          </ol>
        </div>

        <div className={styles['right-column']}>
          <span className={styles['download-title']}>
            4. Download Your Draft:
          </span>
          <ul className={styles['download-instructions']}>
            <li className={styles['instruction-item']}>
              <span className={styles.arrow} aria-hidden="true">
                →
              </span>{' '}
              After the report is generated, you will see a download link. Click
              it to save your draft report to your device.
            </li>
          </ul>

          <div
            className={`${styles['report-status']} ${
              torDoc ? styles.completed : ''
            }`}
          >
            {renderReportStatusContent()}
          </div>
        </div>
      </div>
    </div>
  );
};
